/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace product
 */

import $ from 'jquery';
import {formatMoney} from '@shopify/theme-currency';
import {register} from '@shopify/theme-sections';
import {animations} from '../lib/animations';
import {util} from '../lib/util';
import '../lib/slidescroll.js';

const selectors = {
  addToCart: '[data-add-to-cart]',
  addToCartText: '[data-add-to-cart-text]',
  comparePrice: '[data-compare-price]',
  comparePriceText: '[data-compare-text]',
  originalSelectorId: '[data-product-select]',
  priceWrapper: '[data-price-wrapper]',
  productImageWrapper: '[data-product-image-wrapper]',
  productFeaturedImage: '[data-product-featured-image]',
  productJson: '[data-product-json]',
  productPrice: '[data-product-price]',
  productThumbs: '[data-product-single-thumbnail]',
  variantId: '[data-variant-id]',
};

const cssClasses = {
  activeThumbnail: 'active-thumbnail',
  hide: 'hide',
};

const keyboardKeys = {
  ENTER: 13,
};

/**
 * Product section constructor. Runs on page load as well as Theme Editor
 * `section:load` events.
 * @param {string} container - selector for the section container DOM element
 */

register('product', {
  onLoad() {
    this.$container = $(this.container);
    this.namespace = `.${this.id}`;

    // Run the updateFadeIns function on scroll
    $(window).bind('scroll', () => {
      animations.updateFadeIns(window);
    });

    animations.attachAccordionListeners();

    // Stop parsing if we don't have the product json script tag when loading
    // section in the Theme Editor
    if (!$(selectors.productJson, this.$container).html()) {
      return;
    }

    this.productSingleObject = JSON.parse(
      $(selectors.productJson, this.$container).html(),
    );

    const options = {
      $container: this.$container,
      enableHistoryState: this.$container.data('enable-history-state') || false,
      originalSelectorId: selectors.originalSelectorId,
      product: this.productSingleObject,
    };

    this.settings = {};
    this.$featuredImage = $(selectors.productFeaturedImage, this.$container);

    this.$container.on(
      `variantChange${this.namespace}`,
      this.updateAddToCartState.bind(this),
    );
    this.$container.on(
      `variantPriceChange${this.namespace}`,
      this.updateProductPrices.bind(this),
    );

    if (this.$featuredImage.length > 0) {
      this.$container.on(
        `variantImageChange${this.namespace}`,
        this.updateImages.bind(this),
      );
    }

    // Handle Instant Purchase Button Click
    this.$container.on('click', '[data-scroll-to-form]', this.scrollToForm);

    $('.ca-form-zip-field').on('keyup', () => {
      if ($('.ca-form-zip-field').val().length == 5) {
        $('[data-delivery-date-submit]').prop('disabled', true);
        $('[data-delivery-date-form]').submit();
      } else {
        $('[data-delivery-date-submit]').prop('disabled', false);
        $('.options').addClass('options-modified');

      }
    });

    // Handle Delivery Date Form Submit
    this.$container.on('submit', '[data-delivery-date-form]', this.getDeliveryDate);

    $('.edit-link button').on('click', () => {
      evt.preventDefault();
      util.savingAnimation('.edit-link button', 'Redirecting');
    });

    this.initImageSwitch();
  },

  initImageSwitch() {
    const $productThumbs = $(selectors.productThumbs, this.$container);

    if (!$productThumbs.length) {
      return;
    }

    $productThumbs
      .on('click', (evt) => {
        evt.preventDefault();
        const imageId = $(evt.currentTarget).data('thumbnail-id');
        this.switchImage(imageId);
        this.setActiveThumbnail(imageId);
      })
      .on('keyup', this.handleImageFocus.bind(this));
  },

  handleImageFocus(evt) {
    if (evt.keyCode !== keyboardKeys.ENTER) {
      return;
    }

    this.$featuredImage.filter(':visible').focus();
  },

  setActiveThumbnail(imageId) {
    let newImageId = imageId;

    // If "imageId" is not defined in the function parameter, find it by the current product image
    if (typeof newImageId === 'undefined') {
      newImageId = $(
        `${selectors.productImageWrapper}:not('.${cssClasses.hide}')`,
      ).data('image-id');
    }

    const $thumbnail = $(
      `${selectors.productThumbs}[data-thumbnail-id='${newImageId}']`,
    );

    $(selectors.productThumbs)
      .removeClass(cssClasses.activeThumbnail)
      .removeAttr('aria-current');

    $thumbnail.addClass(cssClasses.activeThumbnail);
    $thumbnail.attr('aria-current', true);
  },

  switchImage(imageId) {
    const $newImage = $(
      `${selectors.productImageWrapper}[data-image-id='${imageId}']`,
      this.$container,
    );
    const $otherImages = $(
      `${selectors.productImageWrapper}:not([data-image-id='${imageId}'])`,
      this.$container,
    );
    $newImage.removeClass(cssClasses.hide);
    $otherImages.addClass(cssClasses.hide);
  },

  /**
   * Updates the DOM state of the add to cart button
   *
   * @param {boolean} enabled - Decides whether cart is enabled or disabled
   * @param {string} text - Updates the text notification content of the cart
   */
  updateAddToCartState(evt) {
    const variant = evt.variant;

    if (variant) {
      $(selectors.variantId).attr('data-variant-id', variant.id);
      $(selectors.priceWrapper, this.$container).removeClass(cssClasses.hide);
    } else {
      $(selectors.addToCart, this.$container).prop('disabled', true);
      $(selectors.addToCartText, this.$container).html(
        theme.strings.unavailable,
      );
      $(selectors.priceWrapper, this.$container).addClass(cssClasses.hide);
      return;
    }

    if (variant.available) {
      $(selectors.addToCart, this.$container).prop('disabled', false);
      $(selectors.addToCartText, this.$container).html(theme.strings.addToCart);
    } else {
      $(selectors.addToCart, this.$container).prop('disabled', true);
      $(selectors.addToCartText, this.$container).html(theme.strings.soldOut);
    }
  },

  updateImages(evt) {
    const variant = evt.variant;
    const imageId = variant.featured_image.id;

    this.switchImage(imageId);
    this.setActiveThumbnail(imageId);
  },

  /**
   * Updates the DOM with specified prices
   *
   * @param {string} productPrice - The current price of the product
   * @param {string} comparePrice - The original price of the product
   */
  updateProductPrices(evt) {
    const variant = evt.variant;
    const $comparePrice = $(selectors.comparePrice, this.$container);
    const $compareEls = $comparePrice.add(
      selectors.comparePriceText,
      this.$container,
    );

    $(selectors.productPrice, this.$container).html(
      formatMoney(variant.price, theme.moneyFormat),
    );

    if (variant.compare_at_price > variant.price) {
      $comparePrice.html(
        formatMoney(variant.compare_at_price, theme.moneyFormat),
      );
      $compareEls.removeClass(cssClasses.hide);
    } else {
      $comparePrice.html('');
      $compareEls.addClass(cssClasses.hide);
    }
  },

  /**
   * Use Mosaic API to get soonest delivery date for a given zip
   */
  getDeliveryDate(evt) {
    evt.preventDefault();

    const zipcode = evt.target.elements.zip_code.value;
    const $form = $(evt.target);
    const $zipError = $form.find('.zip-error');
    const $zipSuccess = $form.find('.form-success');

    $('.options').addClass('options-modified');
    $('.option-loader').show();
    // Remove all errors
    $($zipError).slideUp();

    // If the zipcode submitted is invalid, show error and return button to normal state
    if (util.validZip(zipcode) === null) {
      $($zipError).slideDown();
      return;
    } else {
      $($zipError).slideUp();
    }

    util.getAvailableDeliveryDates({
      zipcode,
      error(err) {
        if (err.status === 422) {
          $($zipError).html('We do not currently serve your zip code.');
          $($zipError).slideDown();
        } else {
          // Let user proceed on any error.
          window.console.log('error', err);
          $('.options').addClass('options-modified');
          $('.option-loader').hide();
        }
      },
      callback(res) {
        const $deliverySelector = $('[data-delivery-date]');
        const delivery_dates = res.map((delivery_date) => { return delivery_date.delivery_date; });
        window.basket.setAvailableDeliveryDates(delivery_dates);
        $('[data-instant-purchase]').attr('data-requested-delivery-date', delivery_dates[0]);
        $('[data-instant-purchase]').attr('data-zip', zipcode);

        const fragment = document.createDocumentFragment();
        delivery_dates.forEach((date) => {
          const parsed_date = util.parseDate(date);
          const opt = document.createElement('option');
          opt.value = date;
          opt.textContent = parsed_date.toLocaleDateString(
              'default',
              {weekday: 'long', month: 'long', day: 'numeric'},
            );
          fragment.appendChild(opt);
        });
        $deliverySelector.html(fragment);

        $('.options').removeClass('options-modified');
        $('.option-loader').hide();
        $('#zip_field').blur();
        $zipSuccess.slideDown();
      },
    });
  },

  /**
   * Scroll To Form
   */
  scrollToForm() {
    $([document.documentElement, document.body]).animate({
      scrollTop: $('#panel-get-started').offset().top,
    }, 750);
  },

  /**
   * Event callback for Theme Editor `section:unload` event
   */
  onUnload() {
    this.$container.off(this.namespace);
  },

});
